import React, { useEffect, useMemo, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
} from "reactstrap"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import axios from "axios"
import apiURl from "url"
import TableContainer from "../../components/Common/TableContainer"

const InternationalSettings = () => {
  document.title = "International Settings"

  // accordion open state
  const [openAcc, setOpenAcc] = useState("1")
  const toggleAcc = id => setOpenAcc(openAcc === id ? "" : id)

  // table state
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  // add/edit modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [editID, setEditID] = useState(null)
  const [statusName, setStatusName] = useState("")
  const [statusColor, setStatusColor] = useState("#22c55e")

  // delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  // ----------------------------
  // API Calls
  // ----------------------------
  const fetchStatuses = async () => {
    try {
      setIsLoading(true)

      // ✅ Keep your existing routes; backend now returns:
      // id, status_name, status_color, created_at
      const { data } = await axios.get(`${apiURl}/international-statuses/list`)

      if (data?.status === "success") {
        setRows(data?.statuses || [])
      } else {
        toastr.error(data?.message || "Failed to fetch statuses")
      }
    } catch (err) {
      toastr.error("Error fetching statuses")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchStatuses()
  }, [])

  const resetForm = () => {
    setEditID(null)
    setStatusName("")
    setStatusColor("#22c55e")
    setModalOpen(false)
  }

  const openAdd = () => {
    setEditID(null)
    setStatusName("")
    setStatusColor("#22c55e")
    setModalOpen(true)
  }

  const openEdit = row => {
    setEditID(row?.id)
    setStatusName(row?.status_name || "")
    setStatusColor(row?.status_color || "#22c55e")
    setModalOpen(true)
  }

  const saveStatus = async e => {
    e.preventDefault()

    if (!statusName.trim()) {
      toastr.error("Status name is required")
      return
    }
    if (!statusColor?.trim()) {
      toastr.error("Color is required")
      return
    }

    try {
      const url = editID
        ? `${apiURl}/international-statuses/update`
        : `${apiURl}/international-statuses/add`

      // ✅ Match new table structure
      const payload = {
        status_name: statusName.trim(),
        status_color: statusColor,
      }
      if (editID) payload.id = editID

      const { data } = await axios.post(url, payload)

      if (data?.status === "success") {
        toastr.success(editID ? "Status updated" : "Status created")
        resetForm()
        fetchStatuses()
      } else {
        toastr.error(data?.message || "Failed to save status")
      }
    } catch (err) {
      toastr.error("Error saving status")
    }
  }

  const deleteStatus = async id => {
    try {
      const { data } = await axios.post(
        `${apiURl}/international-statuses/delete`,
        { id }
      )
      if (data?.status === "success") {
        toastr.success("Status deleted")
        fetchStatuses()
      } else {
        toastr.error(data?.message || "Failed to delete status")
      }
    } catch (err) {
      toastr.error("Error deleting status")
    }
  }

  const handleDelete = () => {
    if (selectedId) deleteStatus(selectedId)
    setShowDeleteModal(false)
    setSelectedId(null)
  }

  // ----------------------------
  // Table Columns
  // ----------------------------
  const columns = useMemo(
    () => [
      {
        Header: "Status Name",
        accessor: "status_name",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Color",
        accessor: "status_color",
        disableFilters: true,
        Cell: ({ value }) => (
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 4,
                background: value || "#999",
                border: "1px solid #ddd",
                display: "inline-block",
              }}
            />
            <small className="text-muted">{value || "—"}</small>
          </div>
        ),
      },
      {
        Header: "Preview",
        accessor: "preview",
        disableFilters: true,
        Cell: ({ row }) => {
          const c = row.original?.status_color || "#999"
          const n = row.original?.status_name || "Status"
          return (
            <Badge pill style={{ background: c }}>
              {n}
            </Badge>
          )
        },
      },
      {
        Header: "Action",
        accessor: "id",
        disableFilters: true,
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              color="primary"
              size="sm"
              onClick={() => openEdit(row.original)}
            >
              <i className="bx bx-pencil" /> Edit
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => {
                setSelectedId(row.original?.id)
                setShowDeleteModal(true)
              }}
            >
              <i className="bx bx-trash" /> Delete
            </Button>
          </div>
        ),
      },
    ],
    [rows]
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <h5 className="mt-2">International Settings</h5>
            <small className="text-muted">
              Configure dynamic values for international forms (statuses, etc.)
            </small>
          </Col>
        </Row>

        <Row className="pt-3">
          <Col lg="12">
            <Accordion open={openAcc} toggle={toggleAcc}>
              {/* ✅ 1) International Statuses */}
              <AccordionItem>
                <AccordionHeader targetId="1">
                  International Statuses
                </AccordionHeader>
                <AccordionBody accordionId="1">
                  <Row className="mb-2">
                    <Col>
                      <p className="text-muted mb-0">
                        Manage statuses used in international workflow/forms.
                      </p>
                    </Col>
                    <Col className="text-end">
                      <Button
                        color="primary"
                        className="btn-sm"
                        onClick={openAdd}
                      >
                        + Create Status
                      </Button>
                    </Col>
                  </Row>

                  <Card className="mt-2">
                    <CardBody>
                      {isLoading ? (
                        <div className="d-flex flex-column align-items-center justify-content-center py-4">
                          <Spinner
                            color="primary"
                            style={{ width: "3rem", height: "3rem" }}
                            className="mb-2"
                          />
                          <small className="text-muted">Loading ....</small>
                        </div>
                      ) : (
                        <TableContainer
                          columns={columns}
                          data={rows}
                          customPageSize={10}
                        />
                      )}
                    </CardBody>
                  </Card>
                </AccordionBody>
              </AccordionItem>

              {/* placeholders for future tasks */}
              {/* <AccordionItem>
                <AccordionHeader targetId="2">
                  (Next Task) International Reasons / Types
                </AccordionHeader>
                <AccordionBody accordionId="2">
                  <div className="text-muted">
                    Coming next: dynamic lists for other international form
                    fields.
                  </div>
                </AccordionBody>
              </AccordionItem>

              <AccordionItem>
                <AccordionHeader targetId="3">
                  (Next Task) International SLA / Rules
                </AccordionHeader>
                <AccordionBody accordionId="3">
                  <div className="text-muted">
                    Coming next: rule-based settings & mapping screens.
                  </div>
                </AccordionBody>
              </AccordionItem> */}
            </Accordion>
          </Col>
        </Row>
      </Container>

      {/* Add/Edit Status Modal */}
      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        centered
      >
        <Form onSubmit={saveStatus}>
          <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
            {editID ? "Edit Status" : "Create Status"}
          </ModalHeader>

          <ModalBody>
            <FormGroup>
              <Label>Status Name *</Label>
              <Input
                type="text"
                value={statusName}
                onChange={e => setStatusName(e.target.value)}
                placeholder="e.g. In Transit"
                required
              />
            </FormGroup>

            <FormGroup className="mt-3">
              <Label>Status Color *</Label>
              <div className="d-flex align-items-center gap-3">
                <Input
                  type="color"
                  value={statusColor}
                  onChange={e => setStatusColor(e.target.value)}
                  style={{ width: 60, height: 40, padding: 2 }}
                />
                <Badge pill style={{ background: statusColor }}>
                  {statusName?.trim() ? statusName.trim() : "Preview"}
                </Badge>
                <small className="text-muted">{statusColor}</small>
              </div>
            </FormGroup>

            {/* ✅ Removed Status dropdown because table has no `status` column */}
          </ModalBody>

          <ModalFooter>
            <Button color="secondary" onClick={resetForm}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {editID ? "Update" : "Save"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(!showDeleteModal)}
        centered
      >
        <ModalHeader toggle={() => setShowDeleteModal(!showDeleteModal)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete this status? This action cannot be
          undone.
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowDeleteModal(!showDeleteModal)}
          >
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default InternationalSettings
